import React from "react";
import { ReactRouter, CodeEditor } from "@atlassiansox/microscopekit";
import Tabs from "@atlaskit/tabs";
import { LogsTab } from "./logs-tab";
import DropdownMenu, { DropdownItem, DropdownItemGroup, } from "@atlaskit/dropdown-menu";
import EmptyState from "@atlaskit/empty-state";
import mapPlanning from "./us-map-planning.svg";
import { capitalizeWord, Divider, getDeploymentId, updateURLParams, } from "./utils";
import { AliasesTab } from "./aliases-tab";
var Select = require("@atlaskit/select").default;
var _a = require("@atlaskit/page"), Grid = _a.Grid, GridColumn = _a.GridColumn;
var Tooltip = require("@atlaskit/tooltip").default;
export var DetailedDeploymentsPage = function (props) {
    var history = ReactRouter.useHistory();
    var location = ReactRouter.useLocation();
    var urlParams = new URLSearchParams(location.search);
    var _a = props.data, microsEnvironments = _a.microsEnvironments, service = _a.service;
    var microsV2 = service.microsV2;
    var paramUpdates = [];
    // All unique shards as Select Options.
    var shardOptions = (microsV2.shards || []).map(function (e) { return ({
        label: "Label: " + capitalizeWord(e.label),
        value: e.label,
    }); });
    // Figure out what the currently selected shard is, based on url params
    var curShard = shardOptions.find(function (option) { var _a; return option.value === ((_a = urlParams.get("label")) === null || _a === void 0 ? void 0 : _a.toLowerCase()); });
    if (!curShard) {
        // Default to the first shard option, if there is no current shard based on url params
        curShard = shardOptions[0];
        paramUpdates.push({ name: "label", val: curShard.value });
    }
    // All unique environments as Select Options.
    var envOptions = Array.from(new Set(microsEnvironments.map(function (e) { return e.type; }))).map(function (e) { return ({
        label: "Env: " + capitalizeWord(e),
        value: e,
    }); });
    // Figure out what the currently selected environment is, based on url params
    var curEnv = envOptions.find(function (option) { var _a; return option.value === ((_a = urlParams.get("env")) === null || _a === void 0 ? void 0 : _a.toUpperCase()); });
    if (!curEnv) {
        // Default to the first env option, if there is no current env based on url params
        curEnv = envOptions[0];
        paramUpdates.push({ name: "env", val: curEnv.value });
    }
    // All unique regions as Select Options.
    var regionOptions = Array.from(new Set(microsEnvironments
        .filter(function (env) { return env.type === curEnv.value; })
        .map(function (option) { return option.region; }))).map(function (e) { return ({
        label: "Region: " + e,
        value: e,
    }); });
    // Figure out what the currently selected region is, based on url params
    var curRegion = regionOptions.find(function (option) { var _a; return option.value === ((_a = urlParams.get("region")) === null || _a === void 0 ? void 0 : _a.toLowerCase()); });
    if (!curRegion) {
        // Default to the first region option, if there is no current region based on url params
        curRegion = regionOptions[0];
        paramUpdates.push({ name: "region", val: curRegion.value });
    }
    // Filter out what micros envs are available based on the current env + region
    var validMicrosEnvs = microsEnvironments.filter(function (e) { return e.type === curEnv.value && e.region === curRegion.value; });
    var envRegionOptions = Array.from(new Set(validMicrosEnvs)).map(function (e) { return ({
        label: "Env-Region: " + e.id,
        value: e.id,
    }); });
    // Figure out what the currently selected env-region is, based on url params
    var curEnvRegion = envRegionOptions.find(function (option) { var _a; return option.value === ((_a = urlParams.get("envRegion")) === null || _a === void 0 ? void 0 : _a.toLowerCase()); });
    if (!curEnvRegion) {
        curEnvRegion = envRegionOptions[0];
        paramUpdates.push({ name: "envRegion", val: curEnvRegion === null || curEnvRegion === void 0 ? void 0 : curEnvRegion.value });
    }
    var envDeploy = (microsV2.shards || []).find(function (shard) { return shard.label === curShard.value; }).environments.items.find(function (item) { return item.name === (curEnvRegion === null || curEnvRegion === void 0 ? void 0 : curEnvRegion.value); });
    // Groups of deployments, like Stable, Persistent, Other, etc.
    var deploymentTypeGroups = envDeploy
        ? [].concat.apply([], Object.entries(envDeploy.deployments)
            .filter(function (_a) {
            var key = _a[0], val = _a[1];
            return key !== "__typename" &&
                (Array.isArray(val) ? val.length > 0 : !!val);
        })
            .map(function (_a) {
            var key = _a[0];
            return ({ title: key });
        })) : [];
    var getDeploymentItemsByType = function (type) {
        if (!envDeploy) {
            return [];
        }
        return Object.entries(envDeploy.deployments)
            .filter(function (_a) {
            var key = _a[0];
            return key === type;
        })
            .map(function (_a) {
            var key = _a[0], val = _a[1];
            return key === "persistent" || key === "other"
                ? val.map(function (dep) {
                    var deploymentId = getDeploymentId(dep.id);
                    return (React.createElement(DropdownItem, { onClick: function () {
                            return history.push(updateURLParams(location.search, [
                                { name: "type", val: key + "-" + deploymentId },
                            ]));
                        } }, deploymentId));
                })
                : [
                    React.createElement(DropdownItem, { onClick: function () {
                            return history.push(updateURLParams(location.search, [
                                { name: "type", val: key },
                            ]));
                        } }, getDeploymentId(val.id)),
                ];
        });
    };
    var typeOptions = envDeploy
        ? [].concat.apply([], Object.entries(envDeploy.deployments)
            .filter(function (_a) {
            var key = _a[0], val = _a[1];
            return key !== "__typename" &&
                (Array.isArray(val) ? val.length > 0 : !!val);
        })
            .map(function (_a) {
            var key = _a[0], val = _a[1];
            return key === "persistent" || key === "other"
                ? val.map(function (dep) {
                    var deploymentId = getDeploymentId(dep.id);
                    return {
                        label: "Type: " + capitalizeWord(key) + " (" + deploymentId + ")",
                        value: key + "-" + deploymentId,
                    };
                })
                : [{ label: "Type: " + capitalizeWord(key), value: key }];
        })) : [];
    // Figure out what the currently selected deployment type is, based on url params
    var curType = typeOptions.find(function (option) { var _a; return option.value === ((_a = urlParams.get("type")) === null || _a === void 0 ? void 0 : _a.toLowerCase()); });
    // If there is no current deployment type, we need to redirect to a default type option
    // If there are literally zero available type options though,
    // we only do the redirect (to delete the urlParam) if the urlParams are set to something inappropriate
    if (!curType && (urlParams.get("type") || typeOptions[0])) {
        curType = typeOptions[0];
        paramUpdates.push({ name: "type", val: curType === null || curType === void 0 ? void 0 : curType.value });
    }
    // If we have any urlParams that need to be updated, do a history push
    if (paramUpdates.length > 0) {
        history.push(updateURLParams(location.search, paramUpdates));
    }
    // static, previous, canary, persistent, other
    var curTypeName = curType === null || curType === void 0 ? void 0 : curType.value.split("-")[0];
    // deployment id, if persistent or other
    var curTypeId = (curTypeName === "persistent" || curTypeName === "other") ? curType === null || curType === void 0 ? void 0 : curType.value.split("-").pop() : undefined;
    var curDeploy = envDeploy &&
        curType &&
        (curType.value.includes("persistent") || curType.value.includes("other")
            ? envDeploy.deployments[curTypeName].find(function (deployment) {
                return getDeploymentId(deployment.id) === curTypeId;
            })
            : envDeploy.deployments[curType.value]);
    var tabs = [
        {
            id: "service-descriptor",
            label: "Service Descriptor",
            content: ((curDeploy === null || curDeploy === void 0 ? void 0 : curDeploy.serviceDescriptor) ? (React.createElement(CodeEditor, { language: "json", uneditable: true, defaultCode: (curDeploy === null || curDeploy === void 0 ? void 0 : curDeploy.serviceDescriptor) ?
                    JSON.stringify(JSON.parse(curDeploy.serviceDescriptor), null, 2) :
                    "" })) : (React.createElement(EmptyState, { header: "No deployments found", description: "", imageUrl: mapPlanning, maxImageWidth: 800, maxImageHeight: 800 }))),
        },
        {
            id: "logs",
            label: "Deployment Logs",
            content: (React.createElement(LogsTab, { serviceId: props.id, curShard: curShard.value, envRegion: curEnvRegion.value, deploymentType: curTypeName, deploymentId: curTypeId })),
        },
        {
            id: "aliases",
            label: "Aliases",
            content: (curType && curShard ? (React.createElement(AliasesTab, { serviceId: props.id, type: curTypeName, deployId: curTypeId, shard: curShard.value, environment: curEnvRegion === null || curEnvRegion === void 0 ? void 0 : curEnvRegion.value.toUpperCase().replace("-", "_") })) : (React.createElement("div", null, "No deployment selected.")))
        }
    ];
    var selectedTab = tabs.find(function (tab) { var _a; return tab.id === ((_a = urlParams.get("tab")) === null || _a === void 0 ? void 0 : _a.toLowerCase()); }) ||
        tabs[0];
    return (React.createElement("div", null,
        React.createElement(Grid, null,
            shardOptions.length > 1 && (React.createElement(GridColumn, { medium: 3 },
                React.createElement(Select, { id: "label-filter", options: shardOptions, value: curShard, onChange: function (_a) {
                        var value = _a.value;
                        return history.push(updateURLParams(location.search, [
                            { name: "label", val: value },
                        ]));
                    }, placeholder: "Label" }))),
            React.createElement(GridColumn, { medium: 3 },
                React.createElement(Select, { id: "env-filter", options: envOptions, value: curEnv, onChange: function (_a) {
                        var value = _a.value;
                        return history.push(updateURLParams(location.search, [{ name: "env", val: value }]));
                    }, placeholder: "Env" })),
            React.createElement(GridColumn, { medium: 3 },
                React.createElement(Select, { id: "region-filter", options: regionOptions, value: curRegion, onChange: function (_a) {
                        var value = _a.value;
                        return history.push(updateURLParams(location.search, [
                            { name: "region", val: value },
                        ]));
                    }, placeholder: "Region" })),
            React.createElement(GridColumn, { medium: 3 },
                React.createElement(Tooltip, { content: envRegionOptions.length <= 1
                        ? "No other possible env-region options"
                        : undefined },
                    React.createElement(Select, { id: "env-region-filter", isDisabled: envRegionOptions.length <= 1, options: envRegionOptions, value: curEnvRegion, onChange: function (_a) {
                            var value = _a.value;
                            return history.push(updateURLParams(location.search, [
                                { name: "envRegion", val: value },
                            ]));
                        }, placeholder: "Env-Region" }))),
            React.createElement(GridColumn, { medium: 3 },
                React.createElement(DropdownMenu, { shouldFitContainer: true, trigger: (curType === null || curType === void 0 ? void 0 : curType.label) || "Type: None", triggerType: "button", triggerButtonProps: {
                        shouldFitContainer: true,
                        style: {
                            fontWeight: 400,
                            minHeight: "40px",
                            alignItems: "center",
                            textAlign: "left",
                        },
                    } }, deploymentTypeGroups.map(function (item) { return (React.createElement(DropdownItemGroup, { title: item.title }, getDeploymentItemsByType(item.title))); })))),
        React.createElement(Divider, null),
        curDeploy ? (React.createElement(Tabs, { onSelect: function (selected) {
                return history.push(updateURLParams(location.search, [
                    { name: "tab", val: selected.id },
                ]));
            }, selected: selectedTab, tabs: tabs })) : (React.createElement("div", null, "No deployment found"))));
};
